import {
  Component,
  HostBinding,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import localeDe from '@angular/common/locales/de';
import { registerLocaleData } from '@angular/common';
import { ThemingService } from 'src/app/util/services/theming.service';
import { Subscription } from 'rxjs';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { DateAdapter } from '@angular/material/core';
import { DE_DATE_ADAPTER_LOCALE } from './util/date/date.util';
import { PwaUpdateService } from './util/pwa/pwa-update.service';

registerLocaleData(localeDe);

const KIOSK_PATHS = ['/board'];

function isKioskPath(path: string): boolean {
  return KIOSK_PATHS.some(
    (kioskPath) => path === kioskPath || path.startsWith(`${kioskPath}/`),
  );
}

function hasKioskData(route: ActivatedRouteSnapshot): boolean {
  let current: ActivatedRouteSnapshot | null = route;
  while (current) {
    if (current.data['kiosk'] === true) return true;
    current = current.firstChild;
  }
  return false;
}

import { NavComponent } from './components/nav/nav.component';
import { NgxSpinnerModule } from 'ngx-spinner';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  imports: [NavComponent, NgxSpinnerModule, RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _cdr = inject(ChangeDetectorRef);
  private themingService = inject(ThemingService);
  private overlayContainer = inject(OverlayContainer);
  private _sanitizer = inject(DomSanitizer);
  private _iconRegistry = inject(MatIconRegistry);
  private _dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  // Eagerly initialize the PWA update service so background update detection
  // starts at app launch rather than lazily when settings is first visited.
  protected readonly _pwaUpdate = inject(PwaUpdateService);

  @HostBinding('class') public cssClass!: string;

  /**
   * Kiosk-Seiten (`data: { kiosk: true }`, z. B. das UserBoard) ohne Navigation
   * und Sitzungsdialoge. Startwert aus der Adresse, damit die Navigation gar nicht erst lädt.
   */
  readonly kiosk = signal(isKioskPath(window.location.pathname));

  themingSubscription!: Subscription;

  constructor() {
    this._dateAdapter.setLocale(DE_DATE_ADAPTER_LOCALE);
    const router = inject(Router);
    router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() =>
        this.kiosk.set(hasKioskData(router.routerState.snapshot.root)),
      );
  }

  ngOnInit() {
    this.themingSubscription = this.themingService.theme.subscribe(
      (theme: string) => {
        this.cssClass = theme;
        this.applyThemeOnOverlays();

        this._cdr.markForCheck();
      },
    );
    this.registerIcons([
      'loeschfahrzeug',
      'drehleiter',
      'wechsellader',
      'anhaenger',
      'gabelstapler',
      'abroller',
      'lkw',
    ]);
  }

  private registerIcons(icons: string[] = []) {
    icons.forEach((elem) => {
      this._iconRegistry.addSvgIcon(
        elem,
        this._sanitizer.bypassSecurityTrustResourceUrl(
          `assets/icons/vehicles/${elem}.svg`,
        ),
      );
    });
  }

  /**
   * Apply the current theme on components with overlay (e.g. Dropdowns, Dialogs)
   */
  private applyThemeOnOverlays() {
    // remove old theme class and add new theme class
    // we're removing any css class that contains '-theme' string but your theme classes can follow any pattern
    const overlayContainerClasses =
      this.overlayContainer.getContainerElement().classList;
    const themeClassesToRemove = Array.from(this.themingService.themes);
    if (themeClassesToRemove.length) {
      overlayContainerClasses.remove(...themeClassesToRemove);
    }
    overlayContainerClasses.add(this.cssClass);
  }

  ngOnDestroy() {
    this.themingSubscription.unsubscribe();
  }
}
